import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { 
    Search, Play, BarChart2, TrendingUp, Activity
} from 'lucide-react';
import apiService from '../../services/apiService';

// Styled Components
const PageContainer = styled.div`
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
`;

const Header = styled.div`
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.025em;
`;

const NavTabs = styled.div`
  display: flex;
  gap: 8px;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 12px;
  width: fit-content;
  margin-bottom: 24px;
`;

const NavTab = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  background: ${props => props.active ? '#ffffff' : 'transparent'};
  color: ${props => props.active ? '#0f172a' : '#64748b'};
  box-shadow: ${props => props.active ? '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' : 'none'};

  &:hover {
    color: ${props => props.active ? '#0f172a' : '#334155'};
    background: ${props => props.active ? '#ffffff' : '#e2e8f0'};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 24px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.05);
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const FormGroup = styled.div`
  flex: 1;
  min-width: 200px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  font-size: 14px;
  background-color: white;
  cursor: pointer;
`;

const StrategyOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${props => props.checked ? '#eff6ff' : '#f8fafc'};
  border: 1px solid ${props => props.checked ? '#3b82f6' : '#e2e8f0'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #eff6ff;
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 12px;
  border: none;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  
  background: ${props => props.variant === 'primary' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : '#f1f5f9'};
  color: ${props => props.variant === 'primary' ? '#ffffff' : '#475569'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

// Progress Section
const ProgressBarContainer = styled.div`
  height: 12px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 12px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
  width: ${props => props.width || '0%'};
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 20px;
`;

const LogContainer = styled.div`
  height: 200px;
  overflow-y: auto;
  background: #1e293b;
  border-radius: 12px;
  padding: 16px;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 12px;
`;

const LogItem = styled.div`
  color: ${props => props.type === 'hit' ? '#10b981' : props.type === 'error' ? '#ef4444' : '#94a3b8'};
  margin-bottom: 4px;
`;

// Result Tables
const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 12px;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  border-bottom: 1px solid #e2e8f0;
`;

const Td = styled.td`
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  color: #1e293b;
`;

const Badge = styled.span`
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  background: ${props => props.bg || '#e2e8f0'};
  color: ${props => props.color || '#475569'};
  margin-right: 4px;
`;

// Summary Cards
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: #ffffff;
  padding: 20px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  text-align: center;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-bottom: 8px;
  font-weight: 600;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 800;
  color: ${props => props.color || '#1e293b'};
`;

const DistributionChart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
`;

const DistRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DistLabel = styled.div`
  width: 80px;
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
`;

const DistBarContainer = styled.div`
  flex: 1;
  height: 20px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
`;

const DistBar = styled.div`
  height: 100%;
  background: ${props => props.bg || '#3b82f6'};
  width: ${props => props.width || '0%'};
  transition: width 0.5s ease-out;
`;

const DistValue = styled.div`
  width: 40px;
  font-size: 12px;
  font-weight: 700;
  color: #1e293b;
`;

const TurtleMultiSimPage = () => {
    const navigate = useNavigate();
    
    // Condition States
    const [scanDate, setScanDate] = useState('');
    const [selectedStrategies, setSelectedStrategies] = useState(['A', 'B', 'C']);
    const [market, setMarket] = useState('ALL');

    // Execution States
    const [scanning, setScanning] = useState(false);
    const [scanProgress, setScanProgress] = useState({ current: 0, total: 100, symbol: '', name: '' });
    const [scanLog, setScanLog] = useState([]);
    
    // Results States
    const [hits, setHits] = useState([]);
    const [selectedHitSymbols, setSelectedHitSymbols] = useState([]);
    const [simulating, setSimulating] = useState(false);
    const [simResults, setSimResults] = useState(null);

    // Initial Date Setup
    useEffect(() => {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        setScanDate(`${yyyy}-${mm}-${dd}`);
    }, []);

    const toggleStrategy = (s) => {
        if (selectedStrategies.includes(s)) {
            setSelectedStrategies(selectedStrategies.filter(item => item !== s));
        } else {
            setSelectedStrategies([...selectedStrategies, s]);
        }
    };

    const handleScreening = async () => {
        if (!scanDate) { alert('기준 날짜를 선택해주세요.'); return; }
        if (selectedStrategies.length === 0) { alert('최소 하나의 전략을 선택해주세요.'); return; }
        
        setScanning(true);
        setHits([]);
        setScanLog([]);
        setSimResults(null);
        setScanProgress({ current: 0, total: 1, symbol: '', name: '' });

        try {
            await apiService.runTurtleMultiScreenerStream(
                scanDate.replace(/-/g, ''),
                selectedStrategies,
                market,
                (data) => {
                    if (data.type === 'start') {
                        setScanProgress(p => ({ ...p, total: data.total }));
                    } else if (data.type === 'progress') {
                        setScanProgress(p => ({ 
                            ...p, 
                            current: data.current, 
                            symbol: data.symbol, 
                            name: data.name 
                        }));
                    } else if (data.type === 'hit') {
                        setScanLog(prev => [{ type: 'hit', symbol: data.symbol, name: data.name, strategies: data.strategies }, ...prev]);
                        setHits(prev => {
                            const newHits = [...prev, data];
                            newHits.sort((a, b) => {
                                const dateA = a.satisfiedDate || a.scanDate;
                                const dateB = b.satisfiedDate || b.scanDate;
                                return dateB.localeCompare(dateA);
                            });
                            return newHits;
                        });
                    } else if (data.type === 'error') {
                        setScanLog(prev => [{ type: 'error', message: `[${data.symbol}] ${data.message}` }, ...prev]);
                    } else if (data.type === 'done') {
                        setScanning(false);
                        setScanProgress(p => ({ ...p, current: p.total }));
                        setSelectedHitSymbols(data.hits.map(h => h.symbol)); // select all by default
                    }
                }
            );
        } catch (err) {
            console.error('Screening Error:', err);
            setScanning(false);
            alert('스크리닝 중 오류 발생: ' + err.message);
        }
    };

    const handleSaveSelected = async () => {
        const selectedHits = hits.filter(h => selectedHitSymbols.includes(h.symbol));
        if (selectedHits.length === 0) { alert('저장할 항목을 선택해주세요.'); return; }
        try {
            await apiService.saveTurtleMultiScreener(selectedHits);
            alert('성공적으로 저장되었습니다.');
        } catch (e) {
            alert('저장 오류: ' + e.message);
        }
    };

    const handleLoadRecent = async () => {
        try {
            const data = await apiService.loadTurtleMultiScreener();
            if (data && data.hits && data.hits.length > 0) {
                setHits(data.hits);
                setSelectedHitSymbols(data.hits.map(h => h.symbol));
                setScanDate(data.hits[0].scanDate.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
                alert('최근 스크리닝 결과를 불러왔습니다.');
            } else {
                alert('저장된 최근 결과가 없습니다.');
            }
        } catch (e) {
            alert('불러오기 오류: ' + e.message);
        }
    };

    const toggleHitSelection = (symbol) => {
        if (selectedHitSymbols.includes(symbol)) {
            setSelectedHitSymbols(prev => prev.filter(s => s !== symbol));
        } else {
            setSelectedHitSymbols(prev => [...prev, symbol]);
        }
    };

    const toggleAllHits = () => {
        if (selectedHitSymbols.length === hits.length) {
            setSelectedHitSymbols([]);
        } else {
            setSelectedHitSymbols(hits.map(h => h.symbol));
        }
    };

    const handleSimulate = async () => {
        const selectedHits = hits.filter(h => selectedHitSymbols.includes(h.symbol));
        if (!selectedHits.length) { alert('시뮬레이션할 스크리닝 결과가 선택되지 않았습니다.'); return; }
        setSimulating(true);
        try {
            const json = await apiService.runTurtleMultiSimulate(selectedHits, scanDate.replace(/-/g, ''));
            if (json.success) {
                setSimResults(json.result);
                // Scroll to results
                setTimeout(() => {
                    document.getElementById('sim-results')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            } else {
                alert('시뮬레이션 오류: ' + json.error);
            }
        } catch (e) {
            alert('오류: ' + e.message);
        } finally {
            setSimulating(false);
        }
    };

    const getReasonInfo = (reason) => {
        const map = {
            'SUPERTREND': { label: 'SuperTrend 이탈', color: '#ef4444' },
            'TRAILING_STOP': { label: '손절 (고점-5%)', color: '#f97316' },
            'END_OF_DATA': { label: '데이터 종료', color: '#64748b' },
        };
        return map[reason] || { label: reason, color: '#94a3b8' };
    };

    const getReturnDistribution = () => {
        if (!simResults) return [];
        const results = simResults.results;
        const dist = [
            { label: '+10% 이상', min: 10, max: Infinity, count: 0, bg: '#059669' },
            { label: '+5~+10%', min: 5, max: 10, count: 0, bg: '#10b981' },
            { label: '0~+5%', min: 0, max: 5, count: 0, bg: '#60a5fa' },
            { label: '-5~0%', min: -5, max: 0, count: 0, bg: '#fb923c' },
            { label: '-10~-5%', min: -10, max: -5, count: 0, bg: '#f97316' },
            { label: '-10% 이하', min: -Infinity, max: -10, count: 0, bg: '#ef4444' },
        ];
        
        results.forEach(r => {
            const ret = r.totalReturn;
            for (let d of dist) {
                if (ret >= d.min && ret < d.max) {
                    d.count++;
                    break;
                }
            }
        });
        
        const total = results.length;
        return dist.map(d => ({ ...d, width: total > 0 ? (d.count / total) * 100 : 0 }));
    };

    return (
        <PageContainer>
            <Header>
                <Title>멀티 지표 복합 시뮬레이션</Title>
            </Header>

            <NavTabs>
                <NavTab onClick={() => navigate('/labs/turtle/dashboard')}>대시보드</NavTab>
                <NavTab onClick={() => navigate('/labs/turtle/positions')}>포지션</NavTab>
                <NavTab onClick={() => navigate('/labs/turtle/journal')}>매매일지</NavTab>
                <NavTab active>멀티 시뮬</NavTab>
                <NavTab onClick={() => navigate('/labs/turtle/screener')}>스크리너</NavTab>
                <NavTab onClick={() => navigate('/labs/turtle/settings')}>설정</NavTab>
            </NavTabs>

            <Grid>
                {/* Section 1: Condition Panel */}
                <Card>
                    <CardTitle><Search size={18} /> 스크리닝 조건 설정</CardTitle>
                    <FormRow>
                        <FormGroup>
                            <Label>기준 날짜 (매수 시점)</Label>
                            <Input type="date" value={scanDate} onChange={e => setScanDate(e.target.value)} disabled={scanning} />
                        </FormGroup>
                        <FormGroup>
                            <Label>시장 필터</Label>
                            <Select value={market} onChange={e => setMarket(e.target.value)} disabled={scanning}>
                                <option value="ALL">전체 시장</option>
                                <option value="KOSPI">코스피</option>
                                <option value="KOSDAQ">코스닥</option>
                            </Select>
                        </FormGroup>
                    </FormRow>

                    <Label>적용 전략 (복수 선택 가능)</Label>
                    <StrategyOptions>
                        <CheckboxLabel checked={selectedStrategies.includes('A')}>
                            <Checkbox type="checkbox" checked={selectedStrategies.includes('A')} onChange={() => toggleStrategy('A')} disabled={scanning} />
                            <div>
                                <div style={{ fontWeight: 700, fontSize: '14px' }}>전략 A: VWAP 복합</div>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>VWAP20 &gt; VWAP72 &gt; SuperTrend</div>
                            </div>
                        </CheckboxLabel>
                        <CheckboxLabel checked={selectedStrategies.includes('B')}>
                            <Checkbox type="checkbox" checked={selectedStrategies.includes('B')} onChange={() => toggleStrategy('B')} disabled={scanning} />
                            <div>
                                <div style={{ fontWeight: 700, fontSize: '14px' }}>전략 B: SMA 복합</div>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>SMA20 &gt; SMA60 &gt; SuperTrend</div>
                            </div>
                        </CheckboxLabel>
                        <CheckboxLabel checked={selectedStrategies.includes('C')}>
                            <Checkbox type="checkbox" checked={selectedStrategies.includes('C')} onChange={() => toggleStrategy('C')} disabled={scanning} />
                            <div>
                                <div style={{ fontWeight: 700, fontSize: '14px' }}>전략 C: 완전 정배열</div>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>VWAP20 &gt; VWAP72 &gt; SMA20 &gt; SMA60</div>
                            </div>
                        </CheckboxLabel>
                    </StrategyOptions>

                    <div style={{ display: 'flex', gap: '12px' }}>
                        <Button variant="primary" onClick={handleScreening} disabled={scanning}>
                            {scanning ? <Activity size={18} className="spin" /> : <Play size={18} />} 
                            {scanning ? '스크리닝 진행 중...' : '스크리닝 시작'}
                        </Button>
                        <Button variant="secondary" onClick={handleLoadRecent} disabled={scanning}>
                            최근 결과 불러오기
                        </Button>
                    </div>
                </Card>

                {/* Section 2: Progress Panel */}
                <Card style={{ opacity: scanning || scanLog.length > 0 ? 1 : 0.5, pointerEvents: scanning || scanLog.length > 0 ? 'auto' : 'none' }}>
                    <CardTitle><Activity size={18} /> 실시간 스캔 정보</CardTitle>
                    <ProgressText>
                        <span>{scanning ? `진행 중: ${scanProgress.name || '심볼 로딩...'}` : '스캔 대기'}</span>
                        <span>{scanProgress.current.toLocaleString()} / {scanProgress.total.toLocaleString()}</span>
                    </ProgressText>
                    <ProgressBarContainer>
                        <ProgressFill width={`${(scanProgress.current / scanProgress.total) * 100}%`} />
                    </ProgressBarContainer>
                    <Label>실시간 히트 로그</Label>
                    <LogContainer>
                        {scanLog.map((log, i) => (
                            <LogItem key={i} type={log.type}>
                                {log.type === 'hit' ? (
                                    <span>[HIT] {log.name} ({log.symbol}) - Strategies: {log.strategies.join(', ')}</span>
                                ) : (
                                    <span>[ERR] {log.message}</span>
                                )}
                            </LogItem>
                        ))}
                    </LogContainer>
                </Card>
            </Grid>

            {/* Section 3: Result Table */}
            {hits.length > 0 && (
                <Card style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                        <CardTitle style={{ margin: 0 }}><Search size={18} /> 스크리닝 결과 ({hits.length}종목)</CardTitle>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <Button variant="secondary" onClick={handleSaveSelected} disabled={scanning || hits.length === 0}>
                                선택 항목 DB 저장
                            </Button>
                            <Button variant="primary" onClick={handleSimulate} disabled={simulating || hits.length === 0}>
                                {simulating ? <Activity size={18} className="spin" /> : <TrendingUp size={18} />}
                                {simulating ? '시뮬레이션 중...' : '선택 항목 시뮬레이션'}
                            </Button>
                        </div>
                    </div>
                    <TableContainer>
                        <Table>
                            <thead>
                                <tr>
                                    <Th style={{ width: '40px', textAlign: 'center' }}>
                                        <Checkbox type="checkbox" checked={selectedHitSymbols.length === hits.length} onChange={toggleAllHits} />
                                    </Th>
                                    <Th>종목명</Th>
                                    <Th>전략/시점</Th>
                                    <Th>기준가</Th>
                                    <Th>VWAP20/72</Th>
                                    <Th>SMA20/60</Th>
                                    <Th>SuperTrend</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {hits.map((h, i) => (
                                    <tr key={i}>
                                        <Td style={{ textAlign: 'center' }}>
                                            <Checkbox type="checkbox" checked={selectedHitSymbols.includes(h.symbol)} onChange={() => toggleHitSelection(h.symbol)} />
                                        </Td>
                                        <Td>
                                            <div 
                                                style={{ fontWeight: 700, cursor: 'pointer', color: '#2563eb', textDecoration: 'underline' }}
                                                onClick={() => window.open('/labs/stock/technical?symbol=' + h.symbol, '_blank')}
                                            >
                                                {h.name}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#64748b' }}>{h.symbol}</div>
                                        </Td>
                                        <Td>
                                            <div>{h.strategies.map(s => <Badge key={s} bg={s === 'A' ? '#dcfce7' : s === 'B' ? '#dbeafe' : '#fef9c3'} color={s === 'A' ? '#166534' : s === 'B' ? '#1e40af' : '#854d0e'}>{s}</Badge>)}</div>
                                            {h.satisfiedDate && <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px', fontWeight: 600 }}>{h.satisfiedDate.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')} 만족</div>}
                                        </Td>
                                        <Td>{h.price.toLocaleString()}원</Td>
                                        <Td>
                                            <div style={{ fontSize: '13px' }}>{Math.round(h.vwap20).toLocaleString()}</div>
                                            <div style={{ fontSize: '11px', color: '#94a3b8' }}>{Math.round(h.vwap72).toLocaleString()}</div>
                                        </Td>
                                        <Td>
                                            <div style={{ fontSize: '13px' }}>{Math.round(h.sma20).toLocaleString()}</div>
                                            <div style={{ fontSize: '11px', color: '#94a3b8' }}>{Math.round(h.sma60).toLocaleString()}</div>
                                        </Td>
                                        <Td>{Math.round(h.stValue).toLocaleString()}</Td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </TableContainer>
                </Card>
            )}

            {/* Section 4: Simulation Results */}
            {simResults && (
                <div id="sim-results">
                    <CardTitle style={{ marginBottom: '20px' }}><BarChart2 size={18} /> 시뮬레이션 리포트 요약</CardTitle>
                    <StatGrid>
                        <StatCard>
                            <StatLabel>총 종목 수 / 승률</StatLabel>
                            <StatValue>{simResults.summary.totalHits}종목 / <span style={{ color: '#10b981' }}>{simResults.summary.winRate}%</span></StatValue>
                        </StatCard>
                        <StatCard>
                            <StatLabel>평균 수익률</StatLabel>
                            <StatValue color={simResults.summary.avgReturn >= 0 ? '#ef4444' : '#2563eb'}>
                                {simResults.summary.avgReturn > 0 ? '+' : ''}{simResults.summary.avgReturn}%
                            </StatValue>
                        </StatCard>
                        <StatCard>
                            <StatLabel>평균 MDD</StatLabel>
                            <StatValue color="#f97316">{simResults.summary.avgMdd}%</StatValue>
                        </StatCard>
                    </StatGrid>

                    <Grid>
                        <Card>
                            <CardTitle>전략별 성능 비교</CardTitle>
                            <Table>
                                <thead>
                                    <tr>
                                        <Th>전략</Th>
                                        <Th>매칭 수</Th>
                                        <Th>승률</Th>
                                        <Th>평균 수익률</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(simResults.summary.byStrategy).map(([key, val]) => (
                                        <tr key={key}>
                                            <Td>전략 {key}</Td>
                                            <Td>{val.count}개</Td>
                                            <Td style={{ fontWeight: 700, color: '#10b981' }}>{val.winRate}%</Td>
                                            <Td style={{ fontWeight: 700, color: val.avgReturn >= 0 ? '#ef4444' : '#2563eb' }}>{val.avgReturn > 0 ? '+' : ''}{val.avgReturn}%</Td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card>
                        <Card>
                            <CardTitle>수익률 분포</CardTitle>
                            <DistributionChart>
                                {getReturnDistribution().map((d, i) => (
                                    <DistRow key={i}>
                                        <DistLabel>{d.label}</DistLabel>
                                        <DistBarContainer>
                                            <DistBar width={`${d.width}%`} bg={d.bg} />
                                        </DistBarContainer>
                                        <DistValue>{d.count}</DistValue>
                                    </DistRow>
                                ))}
                            </DistributionChart>
                        </Card>
                    </Grid>

                    <Card style={{ marginTop: '24px' }}>
                        <CardTitle>종목별 상세 시뮬레이션 결과</CardTitle>
                        <TableContainer>
                            <Table>
                                <thead>
                                    <tr>
                                        <Th>종목</Th>
                                        <Th>매수 (일/가)</Th>
                                        <Th>부분매도 (+5%)</Th>
                                        <Th>최종청산 (일/가)</Th>
                                        <Th>수익률</Th>
                                        <Th>청산 사유</Th>
                                        <Th>MDD</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {simResults.results.map((r, i) => {
                                        const reason = getReasonInfo(r.exitReason);
                                        return (
                                            <tr key={i}>
                                                <Td>
                                                    <div style={{ fontWeight: 700 }}>{r.name}</div>
                                                    <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                                                        {r.strategies.map(s => <Badge key={s} bg="#f1f5f9">{s}</Badge>)}
                                                    </div>
                                                </Td>
                                                <Td>
                                                    <div style={{ fontSize: '13px' }}>{r.entryDate}</div>
                                                    <div style={{ fontSize: '12px', fontWeight: 600 }}>{r.entryPrice.toLocaleString()}원</div>
                                                </Td>
                                                <Td>
                                                    {r.partialSellDate ? (
                                                        <>
                                                            <div style={{ fontSize: '13px', color: '#10b981' }}>{r.partialSellDate}</div>
                                                            <div style={{ fontSize: '12px', fontWeight: 600 }}>{r.partialSellPrice.toLocaleString()}원</div>
                                                        </>
                                                    ) : (
                                                        <span style={{ color: '#94a3b8' }}>-</span>
                                                    )}
                                                </Td>
                                                <Td>
                                                    <div style={{ fontSize: '13px' }}>{r.exitDate}</div>
                                                    <div style={{ fontSize: '12px', fontWeight: 600 }}>{r.exitPrice.toLocaleString()}원</div>
                                                    <div style={{ fontSize: '11px', color: '#64748b' }}>({r.holdingDays}일 보유)</div>
                                                </Td>
                                                <Td>
                                                    <div style={{ fontSize: '16px', fontWeight: 800, color: r.totalReturn >= 0 ? '#ef4444' : '#2563eb' }}>
                                                        {r.totalReturn > 0 ? '+' : ''}{r.totalReturn}%
                                                    </div>
                                                </Td>
                                                <Td>
                                                    <Badge bg={reason.color + '20'} color={reason.color}>{reason.label}</Badge>
                                                </Td>
                                                <Td style={{ color: '#f97316', fontWeight: 600 }}>{r.mdd}%</Td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </TableContainer>
                    </Card>
                </div>
            )}

            <style>{`
                .spin {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </PageContainer>
    );
};

export default TurtleMultiSimPage;
